import { DataChangeEvent } from '@df/core/service/event/data-change-event';
import { IDIfference, isDifference } from '@df/util/fn/difference';
import each from 'lodash-es/each';
import { IWishlistData, Wishlist } from './wishlist';
import { IWishlistItemData, WishlistItem } from './wishlist-item';

type TChange = 'added' | 'removed';

/**
 * Represents an event that occurs when there is a change in the basket data.
 */
export class WishlistDataChangeEvent extends DataChangeEvent<IWishlistData> {
    /**
     * Cache for items changes
     */
    private itemsCache = new Map<TChange, WishlistItem[]>();

    /**
     * Constructs a new instance of the BasketDataChangeEvent class.
     * @param before The basket data before the change.
     * @param after The basket data after the change.
     * @param basket The basket associated with the event.
     */
    constructor(
        before: IWishlistData | undefined,
        after: IWishlistData | undefined,
        readonly wishlist: Wishlist
    ) {
        super(before, after);
    }

    /**
     * Gets the array of item quantity changes for items that were added.
     * @returns An array of `IItemQtyChange` objects representing the added items, or `undefined` if no items were added.
     */
    get addedItems(): WishlistItem[] | undefined {
        return this.getItemChanges('added');
    }

    /**
     * Gets the array of item quantity changes for items that were removed.
     * @returns An array of `IItemQtyChange` objects representing the removed items, or `undefined` if no items were removed.
     */
    get removedItems(): WishlistItem[] | undefined {
        return this.getItemChanges('removed');
    }

    /**
     * Retrieves the item quantity changes based on the specified change type.
     * @param type The type of change ('added' or 'removed').
     * @returns An array of `IItemQtyChange` objects representing the item quantity changes, or `undefined` if no changes of the specified type were found.
     */
    private getItemChanges(type: TChange): WishlistItem[] | undefined {
        if (!this.itemsCache.has(type)) {
            const addedItems: WishlistItem[] = [];
            const removedItems: WishlistItem[] = [];

            const delta = this.before && this.after && this.getChange('items').delta;
            if (delta) {
                each(delta, (change: any) => {
                    const itemChange = change as IDIfference<IWishlistItemData>;
                    if (isDifference(itemChange)) {
                        const from = itemChange.from;
                        const to = itemChange.to;
                        if (from && !to) {
                            removedItems.push(this.toItem(from));
                            return;
                        }
                        if (!from && to) {
                            addedItems.push(this.toItem(to));
                            return;
                        }
                    }
                });
            }
            this.itemsCache.set('added', addedItems);
            this.itemsCache.set('removed', removedItems);
        }
        const changes = this.itemsCache.get(type);
        return changes?.length ? changes : undefined;
    }

    private toItem(itemData: IWishlistItemData): WishlistItem {
        return this.wishlist.getItemById(itemData.id) || this.wishlist.createItem(itemData);
    }
}
